import { Component, OnInit, HostListener } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  GuardsCheckEnd,
  RouteConfigLoadStart,
  RouteConfigLoadEnd,
} from '@angular/router';
import * as _ from 'lodash';
import { SharedService } from './shared/services/shared.service';
import { HttpService } from './shared/services/http.service';
import { ModalsService } from './shared/services/modals.service';
import { UserService } from './shared/services/user.service';
import { DictionaryService } from './shared/services/dictionary.service';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { ReportsService } from './shared/services/reports.service';
import { DemandService } from './shared/services/demand.service';
import { SupplyService } from './shared/services/supply.service';
import { GridService } from './shared/services/grid.service';
import { environment } from 'environments/environment';
import { AuthGuard } from './shared/guards/auth.guard';
import { ReportComponent } from './shared/components/report/report.component';
import { ErrorComponent } from './core/pages/error/error.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  public isLoading: boolean;
  public isError: boolean;
  public isLoadingModule: boolean;
  public errorMessage: string;
  public environment: any;

  // Constructor
  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public sharedService: SharedService,
    public httpService: HttpService,
    public modalsService: ModalsService,
    public userService: UserService,
    public dictionaryService: DictionaryService,
    public ga: GoogleAnalyticsService,
    public reportsService: ReportsService,
    public demandService: DemandService,
    public supplyService: SupplyService,
    public gridService: GridService
  ) {
    this.isLoading = true;
    this.isError = false;
    this.isLoadingModule = false;
    this.errorMessage = '';
    this.environment = environment;
    this.router.events.subscribe((event: any) => {
      let currURL: string;
      let newURL: string;
      if (event instanceof NavigationEnd) {
        // Variables initialization
        currURL = localStorage.getItem('currURL');
        newURL = event.urlAfterRedirects.split('?')[0];
        // Google analytics and event emitter
        if (currURL !== newURL) {
          ga.setPageView(currURL);
        }
        // Close 'my account' menu
        this.sharedService.myAccountMenuToggleStatus = false;
        // Close 'navigate away' modal
        this.sharedService.navigateAwayConfirmation = true;
        // Scroll to top
        window.scrollTo(0, 0);
        // Close the moadls
        this.modalsService.hideModal();
        // Close the side-menu in small layouts
        if (this.sharedService.isMobile() && this.sharedService.sideMenuState !== 'closed') {
          this.sharedService.toggleSideMenu('closed');
        }

        // Save the last visited URL
        if (location.pathname !== '/login') {
          localStorage.setItem('oldURL', currURL);
          localStorage.setItem('currURL', newURL);
        }
        // Set the default page title
        this.sharedService.setPageTitle();
      } else if (event instanceof RouteConfigLoadStart) {
        this.isLoadingModule = true;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.isLoadingModule = false;
      } else if (event instanceof GuardsCheckEnd && event.url !== '/login' && !this.userService.isLoggedIn()) {
        localStorage.setItem('currURL', event.url);
      }
    });
    this.route.queryParams.subscribe((queryParams: any) => {
      if (location.pathname !== '/login') {
        localStorage.setItem('currURLQueryParams', JSON.stringify(queryParams));
      }
    });
  }

  // On initialization
  ngOnInit() {
    this.sharedService.clientTypeByServer =
      location.hostname && location.hostname.indexOf('publisher') > -1 ? 'publisher' : 'dashboard';

    this.getConfiguration().then(
      (res) => {
        this.isLoading = false;
        this.sharedService.getMessagesBus().subscribe((response: any) => {
          if (response.type === 'LOGIN') {
            this.getConfiguration();
          } else if (response.type === 'NETWORK_IS_CHANGING') {
            this.getConfiguration(true, response.data);
          } else if (response.type === 'NEW_SUPPLY_PARTNER') {
            this.getSupplyPartners();
          } else if (response.type === 'NEW_DEMAND_PARTNER') {
            this.getDemandPartners();
          } else if (
            response.type === 'REPORT_SAVED' ||
            response.type === 'REPORT_COPIED' ||
            response.type === 'REPORT_DELETED'
          ) {
            this.getCustomReports(response);
          } else if (response.type === 'BROADCAST_DISMISS') {
            this.dismissUserNotification(response);
          } else if (response.type === 'NETWORK_UPDATED') {
            this.getActiveNetworks();
          } else if (response.type === 'NEW_WHITELISTED_DOMAIN') {
            this.getDomainWhitelist();
          } else if (response.type === 'NEW_VENDOR') {
            this.getVendorsList();
          } else if (response.type === 'NETWORKS_WILL_BE_CHANGED') {
            this.isLoading = true;
          }
        });
        if (!this.sharedService.isEmpty(localStorage.getItem('sideMenuState'))) {
          this.sharedService.toggleSideMenu(localStorage.getItem('sideMenuState'));
        } else {
          localStorage.setItem('sideMenuState', this.sharedService.sideMenuState);
        }
        this.onResize('initialization');
      },
      (err) => {
        this.isError = true;
      }
    );
  }

  getConfiguration(isNetworkChange: boolean = false, networkChangeProps: any = null) {
    return new Promise((resolve, reject) => {
      this.httpService.post('vdsp_ws/getConfiguration').subscribe(
        (response: any) => {
          if (response.networkData) {
            this.parseConfiguration(response).then(
              (res) => {
                this.initializeApplication().then(
                  (resolve_init) => {
                    if (isNetworkChange) {
                      this.sharedService.sendMessage('CONFIGURATION_LOADED', networkChangeProps);
                    }
                    this.isError = false;
                    this.isLoading = false;
                  },
                  (err) => {
                    this.errorMessage = 'Init App Error.';
                    this.isError = true;
                    this.isLoading = false;
                  }
                );
              },
              (msg) => {
                this.errorMessage = 'Parse Configuration Error.' + msg;
                this.isError = true;
                this.isLoading = false;
              }
            );
          } else {
            if (response.status && response.status === 'FAILED') {
              this.sharedService.sendMessage('LOGIN_FAILURE', response.errors);
            }
            this.isLoading = false;
          }
        },
        (error: any) => {
          this.errorMessage = 'Get Configuration Service Error.';
          this.isError = true;
          this.isLoading = false;
        }
      );
      resolve();
    });
  }

  parseConfiguration(response: any, section: string = null) {
    return new Promise((resolve, reject) => {
      let networkData: any;
      let oneResponce: any;

      if (section === null) {
        // Network data
        networkData = _.cloneDeep(response.networkData);
        networkData.networkId = networkData.networkId.toString();
        networkData.network_domains.forEach((domain: any) => {
          domain.domainId = parseInt(domain.domainId);
          domain.domain_type = parseInt(domain.domain_type);
        });
        this.userService.saveNetworkData(networkData);

        // Targeting
        if (response.targetingLookups) {
          response.targetingLookups.countries.forEach((country: any) => {
            country.name = this.sharedService.fixCase(country.name);
          });

          this.sharedService.targetingLookups = {
            countries: response.targetingLookups.countries,
            iasIncludeList: response.targetingLookups.iasIncludeList,
            iasExcludeList: response.targetingLookups.iasExcludeList,
            browsers: response.targetingLookups.browsers,
            state: response.targetingLookups.state,
            os: response.targetingLookups.os,
            targetingSizes: response.targetingLookups.targetingSizes,
            browserVersions: response.targetingLookups.browserVersions,
            osVersions: response.targetingLookups.osVersions,
            DMAs: response.targetingLookups.DMAs,
            schains: response.targetingLookups.schains || [],
            weekDays: ['Everyday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            hours12Format: [
              '12:00 AM',
              '01:00 AM',
              '02:00 AM',
              '03:00 AM',
              '04:00 AM',
              '05:00 AM',
              '06:00 AM',
              '07:00 AM',
              '08:00 AM',
              '09:00 AM',
              '10:00 AM',
              '11:00 AM',
              '12:00 PM',
              '01:00 PM',
              '02:00 PM',
              '03:00 PM',
              '04:00 PM',
              '05:00 PM',
              '06:00 PM',
              '07:00 PM',
              '08:00 PM',
              '09:00 PM',
              '10:00 PM',
              '11:00 PM',
            ],
          };

          const max_custom_player_size_targeting = this.userService.getFeatureState('max_custom_player_size_targeting')
            ? 20
            : 5;
          for (let i = 1; i <= max_custom_player_size_targeting; i++) {
            this.sharedService.targetingLookups.targetingSizes.push({
              name: this.dictionaryService.get('CUSTOM') + i.toString(),
              custom: true,
              operator: this.sharedService.equationOperators[0],
              width: 0,
              height: 0,
            });
          }
        }

        // Supply/Demand Extra Params
        if (response.extraParamsDefs) {
          if (response.extraParamsDefs.hasOwnProperty(this.sharedService.entityTypeSupply)) {
            this.supplyService.extraParamsDefaultValue = response.extraParamsDefs[this.sharedService.entityTypeSupply];
          }
          if (response.extraParamsDefs.hasOwnProperty(this.sharedService.entityTypeDemand)) {
            this.demandService.extraParamsDefaultValue = response.extraParamsDefs[this.sharedService.entityTypeDemand];
          }
        }

        // Supply/Demand Settings
        this.setDemandTypes(response.demandTypes);
        this.setSupplyTypes(response.supplyTypes);
        this.setSupplySubTypes(response.supplySubTypes);
        this.setCappingSettings(response.cappingLimits);
        this.setEnvTypes(response.envTypes);
        this.setContentUnitTagDefaults(response.contentUnitTagDefaults);
        this.setBidders(response.bidders);
      }

      // Supply Partners
      if (section === null || section === 'supplyPartners') {
        if (response.supplyPartners) {
          response.supplyPartners.forEach((partner: any) => {
            partner.partnerId = parseInt(partner.partnerId);
          });
          this.sharedService.supplyPartners = _.orderBy(_.cloneDeep(response.supplyPartners), 'name');
        }
      }
      // Demand Partners
      if (section === null || section === 'demandPartners') {
        if (response.demandPartners) {
          response.demandPartners.partners_list.forEach((partner: any) => {
            partner.id = parseInt(partner.id);
          });
          response.demandPartners.rtb_vendors.forEach((partner: any) => {
            partner.id = parseInt(partner.id);
          });
          this.sharedService.demandPartners = _.orderBy(_.cloneDeep(response.demandPartners.partners_list), 'name');

          this.sharedService.rtbVendors = _.orderBy(_.cloneDeep(response.demandPartners.rtb_vendors), 'name');
          this.sharedService.rtbVendors.unshift({
            id: 0,
            name: this.dictionaryService.get('CHOOSE') + ' ' + this.dictionaryService.get('RTB_PARTNER'),
          });
        }
      }
      // JS Callbacks
      if (section === null) {
        if (response.jsCallbackDomains) {
          response.jsCallbackDomains.forEach((domain: any) => {
            domain.id = parseInt(domain.id);
          });
          this.sharedService.jsCallbackDomains = _.cloneDeep(response.jsCallbackDomains);
        }
      }
      // Dimensions
      if (section === null) {
        if (response.dimensions_v1) {
          this.reportsService.dimensions_v1 = _.map(
            _.filter(response.dimensions_v1, { isDimension: '1' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
          this.reportsService.pseudoDimensions_v1 = _.map(
            _.filter(response.dimensions_v1, { isDimension: '0' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
        }
      }
      // Dimensions 2.1
      if (section === null) {
        if (response.dimensions_v2) {
          oneResponce = _.cloneDeep(response.dimensions_v2);
          if (this.userService.isCedatoPublisher()) {
            oneResponce.forEach((one: any) => {
              if (one.name.indexOf('Supply') > -1) {
                one.name = one.name.replace(/supply/gi, 'Inventory');
              }
            });
          }

          this.reportsService.dimensions_v2 = _.map(_.filter(oneResponce, { isDimension: '1' }), (one: any) => {
            return _.omit(one, ['isDimension']);
          });

          this.reportsService.pseudoDimensions_v2 = _.map(_.filter(oneResponce, { isDimension: '0' }), (one: any) => {
            return _.omit(one, ['isDimension']);
          });
        }
      }
      // Measures
      if (section === null) {
        if (response.measures_v1) {
          this.gridService.measuresOptions['1'] = response.measures_v1;
        }
        if (response.measures_v2) {
          oneResponce = _.cloneDeep(response.dimensions_v2);
          if (this.userService.isCedatoPublisher()) {
            oneResponce.forEach((one: any) => {
              if (one.name.indexOf('Cost') > -1) {
                one.name = one.name.replace(/cost/gi, 'Revenue');
              }
            });
          }
          this.gridService.measuresOptions['2'] = response.measures_v2;
        }
      }
      // Reports Date Rsange Limits
      if (section === null && response.networkData.labs_agg_table_ttl) {
        this.reportsService.labsDaysLimit = +response.networkData.labs_agg_table_ttl;
      }

      //Globacl prebid URL
      if (section == null) {
        this.sharedService.globalPrebidUrl = response.prebid_url;
      }

      if (section === null && response.networkData.legacy_all_table_ttl) {
        this.reportsService.legacyAllDaysLimit = +response.networkData.legacy_all_table_ttl;
      }

      if (section === null && response.networkData.legacy_by_table_ttl) {
        this.reportsService.legacyByDaysLimit = +response.networkData.legacy_by_table_ttl;
      }

      // Cost-types
      if (section === null) {
        this.sharedService.costTypes = _.cloneDeep(response.costTypes);
      }
      // Custom reports
      if (section === null || section === 'customReports') {
        const routeConfig = this.router.config;
        routeConfig.splice(-1, 1);
        this.reportsService.customReports = [];
        _.forEach(response.customReports, (one) => {
          one.id = parseInt(one.reportId);
          one.name = one.reportName;
          one.link = `reports/${one.reportName}`;
          one.d = one.dimensions.split('--').map(Number);
          one.labsVer = 1;
          one.admin = false;

          this.reportsService.customReports.push(one);

          routeConfig.push({
            path: `reports/${one.reportName}`,
            component: ReportComponent,
            canActivate: [AuthGuard],
            pathMatch: 'full',
            data: {
              isAdmin: false,
            },
          });
        });
        routeConfig.push({ path: '**', component: ErrorComponent });
        this.router.resetConfig(routeConfig);
        this.route.queryParams.subscribe((params) => {
          if (params.id && +params.id > 0 && !this.sharedService.isDynamicRoutesLoaded) {
            this.router.navigate([], {
              queryParams: params,
            });
          }
          this.sharedService.isDynamicRoutesLoaded = true;
        });
      }

      // Notifications
      if (section === null) {
        this.sharedService.userMessages = _.cloneDeep(response.notifications);
      }
      // Admin-related data
      if (this.userService.isAdmin()) {
        if (section === null) {
          // Active networks
          this.sharedService.activeNetworks = _.cloneDeep(response.activeNetworks);
          // Admin dimensions
          this.reportsService.adminDimensions_v1 = _.map(
            _.filter(response.adminDimensions_v1, { isDimension: '1' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
          this.reportsService.adminDimensions_v2 = _.map(
            _.filter(response.adminDimensions_v2, { isDimension: '1' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
          this.reportsService.adminPseudoDimensions_v1 = _.map(
            _.filter(response.adminDimensions_v1, { isDimension: '0' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
          this.reportsService.adminPseudoDimensions_v2 = _.map(
            _.filter(response.adminDimensions_v2, { isDimension: '0' }),
            (one: any) => {
              return _.omit(one, ['isDimension']);
            }
          );
        }
        // Domain whitelist
        if (section === null || section === 'domainWhitelist') {
          this.sharedService.domainWhitelist = _.cloneDeep(response.domainWhitelist);
        }
        // Vendors
        if (section === null || section === 'vendorsList') {
          this.sharedService.vendorsList = _.cloneDeep(response.vendorsList);
          this.sharedService.sendMessage('VENDORS_LIST_UPDATED');
        }
        // Roles
        if (section === null) {
          this.userService.userRoles = response.roles;
        }
      }

      resolve();
    });
  }

  setBidders(bidders: Array<any>) {
    if (bidders && Array.isArray(bidders)) {
      this.demandService.bidders = [];
      bidders.forEach((bidder) => {
        //validate bidder data
        if (bidder.bidderId) {
          const currBidder = {
            value: +bidder.bidderId,
            label: bidder.displayName,
            alias: bidder.name,
          };
          this.demandService.bidders.push(currBidder);
        }
      });
    }
  }

  initializeApplication() {
    return new Promise((resolve, reject) => {
      let currURL: string = localStorage.getItem('currURL');
      if (!this.sharedService.isEmpty(currURL) && currURL.includes('?')) {
        currURL = currURL.split('?')[0];
      }
      let currURLQueryParams: any = localStorage.getItem('currURLQueryParams');
      currURL = this.sharedService.isEmpty(currURL) ? this.userService.getDefaultRoute() : currURL;
      currURLQueryParams = this.sharedService.isEmpty(currURLQueryParams) ? {} : JSON.parse(currURLQueryParams);
      if (currURL.indexOf('?') > -1 && this.sharedService.isEmpty(currURLQueryParams)) {
        if (currURL.split('?').length > 1) {
          [currURL, currURLQueryParams] = currURL.split('?');
          if (currURLQueryParams.length) {
            try {
              currURLQueryParams = JSON.parse(
                '{"' +
                  decodeURI(currURLQueryParams).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') +
                  '"}'
              );
            } catch (e) {
              currURLQueryParams = {};
            }
          }
        }
      }
      if (location.pathname === '/login') {
        this.router.navigate([currURL], { queryParams: currURLQueryParams });
      }
      this.sharedService.sendMessage('NETWORK_CHANGE', { networkId: this.userService.networkData.networkId });
      this.showUserMessages();
      resolve();
    });
  }

  showUserMessages() {
    let type: string;
    this.sharedService.userMessages.forEach((message: any) => {
      if (message.color === '1') {
        type = 'success';
      } else if (message.color === '2') {
        type = 'warning';
      } else if (message.color === '0') {
        type = 'error';
      }
      this.sharedService.showFlashMessage(message.message, type, false, true, message.id);
    });
  }

  getSupplyPartners() {
    this.httpService.post('vdsp_ws/getSupplyPartners').subscribe(
      (response: any) => {
        this.parseConfiguration({ supplyPartners: response.partners_list }, 'supplyPartners').then(
          (res) => {
            this.sharedService.sendMessage('SUPPLY_PARTNERS_UPDATED');
          },
          (error) => {
            this.isError = true;
            this.isLoading = false;
          }
        );
      },
      (error: any) => {
        this.errorMessage = 'Get Supply Partners Error.';
        this.isError = true;
        this.isLoading = false;
      }
    );
  }

  getDemandPartners() {
    this.httpService.post('vdsp_ws/getDemandPartners').subscribe(
      (response: any) => {
        this.parseConfiguration({ demandPartners: response }, 'demandPartners').then(
          (res) => {
            this.sharedService.sendMessage('DEMAND_PARTNERS_UPDATED');
          },
          (error) => {
            this.isError = true;
            this.isLoading = false;
            // Always notify waiters (e.g. demand save) so they do not hang forever
            this.sharedService.sendMessage('DEMAND_PARTNERS_UPDATED');
          }
        );
      },
      (error: any) => {
        this.errorMessage = 'Get Demand Partners Error.';
        this.isError = true;
        this.isLoading = false;
        // Always notify waiters (e.g. demand save) so they do not hang forever
        this.sharedService.sendMessage('DEMAND_PARTNERS_UPDATED');
      }
    );
  }

  getCustomReports(messageBus: any) {
    this.httpService.post('users_management/getCustomReports').subscribe(
      (response: any) => {
        this.parseConfiguration({ customReports: response }, 'customReports').then(
          (res) => {
            let queryParams: any = {};

            if (messageBus.type === 'REPORT_DELETED') {
              queryParams = messageBus.data;
            } else {
              queryParams.id = messageBus.data;
            }

            _.forEach(response, (one) => {
              one.reportId = +one.reportId;
              delete one.userId;
            });

            this.sharedService.sendMessage('CUSTOM_REPORTS_UPDATED', queryParams);
          },
          (error) => {
            this.isError = true;
            this.isLoading = false;
          }
        );
      },
      (error: any) => {
        this.errorMessage = 'Custom Reports Error.';
        this.isError = true;
        this.isLoading = false;
      }
    );
  }

  getActiveNetworks() {
    this.httpService.post('networks_management/getActiveNetworks').subscribe(
      (response: any) => {
        this.parseConfiguration({ activeNetworks: response }, 'activeNetworks');
      },
      (error: any) => {
        this.errorMessage = 'Get Active Networks Error.';
        this.isError = true;
        this.isLoading = false;
      }
    );
  }

  getDomainWhitelist() {
    this.httpService.post('vdsp_ws/getDomainWhitelist').subscribe(
      (response: any) => {
        this.parseConfiguration({ domainWhitelist: response.whitelist }, 'domainWhitelist').then(
          (res) => {
            this.sharedService.sendMessage('DOMAIN_WHITELIST_UPDATED');
          },
          (error) => {
            this.isError = true;
            this.isLoading = false;
          }
        );
      },
      (error: any) => {
        this.errorMessage = 'Get Domain White List Error.';
        this.isError = true;
        this.isLoading = false;
      }
    );
  }

  getVendorsList() {
    this.httpService.post('vdsp_ws/getVendorsList').subscribe(
      (response: any) => {
        this.parseConfiguration({ vendorsList: response.vendors }, 'vendorsList').then(
          (res) => {
            this.sharedService.sendMessage('VENDORS_LIST_UPDATED');
          },
          (error) => {
            this.isError = true;
            this.isLoading = false;
          }
        );
      },
      (error: any) => {
        this.errorMessage = 'Get Vendor List Error.';
        this.isError = true;
        this.isLoading = false;
      }
    );
  }

  dismissUserNotification(response: any) {
    this.httpService.post('notifications/dismissUserNotification', { id: response.data }).subscribe(
      (response: any) => {
        // Message dismissed
      },
      (error: any) => {
        this.sharedService.showErrorFlashMessage();
      }
    );
  }

  setDemandTypes(demands: Array<any>) {
    if (demands && Array.isArray(demands)) {
      this.demandService.demandTypes = [];
      _.forEach(demands, (demand) => {
        let oneDemandType: any = {};
        if (demand.id && demand.name) {
          if (_.find(this.demandService.demandTypeSettings, { id: +demand.id })) {
            oneDemandType = _.find(this.demandService.demandTypeSettings, { id: +demand.id });
          } else {
            oneDemandType.id = +demand.id;
          }
          oneDemandType.name = demand.name;
          oneDemandType.view = this.dictionaryService.get(demand.name);
        }
        this.demandService.demandTypes.push(oneDemandType);
      });
    }
  }

  setSupplyTypes(supplies: Array<any>) {
    if (supplies && Array.isArray(supplies)) {
      this.supplyService.supplyTypes = [];
      _.forEach(supplies, (supply) => {
        let oneSupplyType: any = {};
        if (supply.id && supply.name) {
          if (_.find(this.supplyService.supplyTypeSettings, { id: +supply.id })) {
            oneSupplyType = _.find(this.supplyService.supplyTypeSettings, { id: +supply.id });
          } else {
            oneSupplyType.id = +supply.id;
          }
          oneSupplyType.name = supply.name;
        }
        this.supplyService.supplyTypes.push(oneSupplyType);
      });
    }
  }

  setSupplySubTypes(subTypes: Array<any>) {
    if (subTypes && Array.isArray(subTypes)) {
      this.supplyService.subTypes = [];
      _.forEach(subTypes, (subType) => {
        let oneSubType: any = {};
        //Add here later if you want to add some logic
        //Currently adding as is.
        oneSubType = subType;
        oneSubType.id = +subType.id;
        oneSubType.view = this.dictionaryService.get('SUPPLY_SUB_TYPE_' + subType.name.replace(' ', '_').toUpperCase());
        this.supplyService.subTypes.push(oneSubType);
      });
    }
  }

  setContentUnitTagDefaults(tagDefaults: Array<any>) {
    this.supplyService.contentUnitTagDefaults = [];
    _.forEach(tagDefaults, (tagDefault) => {
      tagDefault.id = this.sharedService.parseNumeric(tagDefault.id);
      let translatedUnitName: string = 'SUPPLY_CONTENT_UNIT_TAG_TYPE_' + tagDefault.template_name.toUpperCase();
      translatedUnitName = this.dictionaryService.get(translatedUnitName);
      tagDefault.template_name = translatedUnitName ? translatedUnitName : tagDefault.template_name;
      this.supplyService.contentUnitTagDefaults.push(tagDefault);
    });
  }

  setCappingSettings(capLimits: Array<any>) {
    if (capLimits && _.isPlainObject(capLimits)) {
      this.demandService.cappingLimits = {};
      this.supplyService.cappingLimits = {};
      _.forEach(capLimits, (val, key) => {
        if (key.includes('demand')) {
          this.demandService.cappingLimits[key] = val;
        } else if (key.includes('supply')) {
          this.supplyService.cappingLimits[key] = val;
        }
      });
    }

    if (this.userService.getFeatureState('vast_capping_freq_user')) {
      this.demandService.cappingTypeSettings.push({ id: 3, name: this.dictionaryService.get('USER_ID_CAPPING') });
    } else {
      if (
        _.find(this.demandService.cappingTypeSettings, (one) => {
          return [3].includes(one.id);
        })
      ) {
        this.demandService.cappingTypeSettings = _.filter(this.demandService.cappingTypeSettings, (one) => {
          return ![3].includes(one.id);
        });
      }
    }

    if (this.userService.getFeatureState('vast_capping_freq_ip')) {
      this.demandService.cappingTypeSettings.push({ id: 4, name: this.dictionaryService.get('IP') });
    } else {
      if (
        _.find(this.demandService.cappingTypeSettings, (one) => {
          return [4].includes(one.id);
        })
      ) {
        this.demandService.cappingTypeSettings = _.filter(this.demandService.cappingTypeSettings, (one) => {
          return ![4].includes(one.id);
        });
      }
    }
  }

  setEnvTypes(envs: Array<any>) {
    if (envs && Array.isArray(envs)) {
      this.sharedService.envTypes = [];
      _.forEach(envs, (env) => {
        if (+env.id > 0) {
          env.view = this.dictionaryService.get(env.name);
          this.sharedService.envTypes.push(env);
        }
      });
    }
  }

  // On resize event-handling
  @HostListener('window:resize', ['$event'])
  onResize($event: any = null) {
    const oldLayout: string = this.sharedService.layout;
    if (!this.sharedService.isMobile() || $event === 'orientationchange' || $event === 'initialization') {
      setTimeout(() => {
        // Update the saved layout
        if (window.innerWidth <= 576) {
          this.sharedService.layout = 'small';
        } else if (window.innerWidth <= 1024) {
          this.sharedService.layout = 'medium';
        } else {
          this.sharedService.layout = 'large';
        }
        if (this.sharedService.layout !== oldLayout) {
          this.sharedService.sendMessage('LAYOUT_SET', this.sharedService.layout);
        }
        // If this is not the initialization event
        if ($event !== 'initialization') {
          // If the current layout is not 'large', and the side-menu is opened...
          if (this.sharedService.layout !== 'large' && this.sharedService.sideMenuState !== 'closed') {
            // ...then close it
            this.sharedService.toggleSideMenu('closed');
            // If the current layout is 'large', and we need to open the menu ('closed' in big layout or on actual resize)
          } else if (this.sharedService.layout === 'large' && this.sharedService.sideMenuState !== 'big') {
            // ...then open it
            this.sharedService.toggleSideMenu('big');
          }
        } else {
          // Reset to default values in edge cases
          if (this.sharedService.sideMenuState === 'big' && this.sharedService.layout !== 'large') {
            this.sharedService.toggleSideMenu('closed');
          }
        }
      }, 100);
    }
  }

  // On orientation change event-handling
  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange($event: any) {
    this.onResize('orientationchange');
  }

  // On local storage changes (handle multi-tab networks change events)
  @HostListener('window:storage', ['$event'])
  onStorageChange($event: any) {
    let diff: any;
    let networkId: number;
    let network: any;
    if (this.userService.isAdmin() && $event.key === 'networkData') {
      diff = this.sharedService.deepDiff(JSON.parse($event.oldValue), JSON.parse($event.newValue));
      if (diff.networkId) {
        networkId = JSON.parse($event.newValue).networkId;
        if (this.sharedService.isNumeric(networkId) && networkId !== this.userService.networkData.networkId) {
          network = _.find(this.sharedService.activeNetworks, { networkId: networkId });
          if (network) {
            this.userService.aboutToChangeNetwork = true;
          }
        }
      }
    }
  }
}
